/**
 * Recovery for stale/missing lazy-loaded chunks (deploy while a tab is open,
 * a momentary network blip). Every browser dependency is injectable so this
 * module is testable under vitest's node environment.
 */

const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css for/i,
];

const STORAGE_KEY = 'lp:chunk-reload-at';
const DEFAULT_WINDOW_MS = 30_000;

let reloading = false;

export function isChunkLoadError(err: unknown): boolean {
  if (err == null) return false;

  if (typeof err === 'object' && 'name' in err && (err as { name?: unknown }).name === 'ChunkLoadError') {
    return true;
  }

  let message: string | undefined;
  if (typeof err === 'string') {
    message = err;
  } else if (typeof err === 'object' && 'message' in err && typeof (err as { message?: unknown }).message === 'string') {
    message = (err as { message: string }).message;
  }

  if (!message) return false;

  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

interface ReloadDeps {
  storage?: Pick<Storage, 'getItem' | 'setItem'>;
  reload?: () => void;
  now?: () => number;
  windowMs?: number;
  navigationType?: () => string | undefined;
}

function defaultNavigationType(): string | undefined {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return entry?.type;
  } catch {
    return undefined;
  }
}

// Route-level page chunks currently loading. Only failures during one of these
// may auto-reload; feature imports (e.g. mammoth) must never discard user work.
let routeLoadsInFlight = 0;
let routeChunkErrors = new WeakSet<object>();

export function markRouteTransition(): () => void {
  routeLoadsInFlight++;
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    routeLoadsInFlight--;
  };
}

export function isRouteLoadInFlight(): boolean {
  return routeLoadsInFlight > 0;
}

/** Wrap a React.lazy import factory so its chunk load counts as a route transition. */
export function lazyRouteImport<T>(factory: () => Promise<T>): () => Promise<T> {
  return async () => {
    const end = markRouteTransition();
    try {
      return await factory();
    } catch (err) {
      if (err !== null && typeof err === 'object') routeChunkErrors.add(err);
      throw err;
    } finally {
      end();
    }
  };
}

export function isRouteChunkError(err: unknown): boolean {
  return err !== null && typeof err === 'object' && routeChunkErrors.has(err) && isChunkLoadError(err);
}

/** Whether an automatic reload is currently permitted; commit=true records it. */
function permitReload(deps: ReloadDeps, commit: boolean): boolean {
  const currentTime = (deps.now ?? Date.now)();
  const windowMs = deps.windowMs ?? DEFAULT_WINDOW_MS;

  try {
    const storage = deps.storage ?? (typeof window !== 'undefined' ? window.sessionStorage : undefined);

    if (storage) {
      const lastReloadAt = storage.getItem(STORAGE_KEY);
      if (lastReloadAt !== null && currentTime - Number(lastReloadAt) < windowMs) {
        return false;
      }
      if (commit) storage.setItem(STORAGE_KEY, String(currentTime));
      return true;
    }
  } catch {
    // Storage unavailable or inaccessible (e.g. Safari private mode, or a
    // SecurityError just reading window.sessionStorage) — fall through to
    // the in-memory guard.
  }

  // Storage is unusable, so the in-memory flag dies with every page load. If
  // this load was itself a reload, a previous life already reloaded: stop.
  if (reloading) return false;
  return (deps.navigationType ?? defaultNavigationType)() !== 'reload';
}

export function canReloadForChunkError(deps: ReloadDeps = {}): boolean {
  return permitReload(deps, false);
}

export function reloadOnceForChunkError(deps: ReloadDeps = {}): boolean {
  if (!permitReload(deps, true)) return false;
  const reload = deps.reload ?? (() => window.location.reload());
  reloading = true;
  reload();
  return true;
}

export function isReloadingForChunkError(): boolean {
  return reloading;
}

export type ErrorScreen = 'updating' | 'chunk-fault' | 'fault';

export function errorScreenFor(error: unknown, reloading: boolean): ErrorScreen {
  if (reloading) return 'updating';
  return isChunkLoadError(error) ? 'chunk-fault' : 'fault';
}

export function installChunkErrorRecovery(win: Pick<Window, 'addEventListener'> = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    if (isRouteLoadInFlight() && reloadOnceForChunkError()) {
      event.preventDefault();
    }
  });
}

export function __resetChunkReloadStateForTests(): void {
  reloading = false;
  routeLoadsInFlight = 0;
  routeChunkErrors = new WeakSet<object>();
}
